import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';

import { DialogModule } from 'primeng/dialog';
import { AvatarModule } from 'primeng/avatar';
import { Table, TableModule } from 'primeng/table';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';

import { BadgeModule } from 'primeng/badge';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';

import * as XLSX from 'xlsx';

import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUser } from '../user.model';
import { UserService } from '../users.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Page } from '../../models/page-wrapper.model';
import { TableLazyLoadEvent } from 'primeng/table';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';

@Component({
  selector: 'app-list-users',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    AvatarModule,
    TableModule,
    BadgeModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    IftaLabelModule,
    TagModule,
    ToastModule,
    ReactiveFormsModule,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './list-users.component.html',
  styleUrl: './list-users.component.css',
})
export class ListUsersComponent implements OnInit {
  @ViewChild('dt') dt!: Table;
  users: AppUser[] = [];
  totalRecords = 0;
  isLoading: boolean = false;
  filterForm!: FormGroup;
  filterSub!: Subscription;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService
  ) {}

  getStatusColor(status: string) {
    switch (status) {
      case 'pending_verification':
        return 'danger';
      case 'diactivated':
        return 'warn';
      case 'inactive':
        return 'danger';
      case 'active':
        return 'info';
      default:
        return 'danger';
    }
  }

  ngOnInit() {
    this.buildFilterForm();
    this.filterSub = this.filterForm.valueChanges
      .pipe(
        debounceTime(500), // Wait for 500ms of inactivity
        distinctUntilChanged() // Only emit if the value has changed
      )
      .subscribe(() => {
        // Reset paginator to the first page and trigger a reload
        this.dt.reset();
      });
  }

  buildFilterForm() {
    this.filterForm = this.fb.group({
      fullName: [''],
      email: [''],
    });
  }

  onSearch(): void {
    this.dt.reset();
  }

  onReset(): void {
    this.filterForm.reset({
      fullName: '',
      email: '',
    });
    this.dt.reset();
  }

  loadUsers(
    event?: TableLazyLoadEvent,
    isExport: boolean = false,
    callback?: (users: AppUser[]) => void
  ) {
    let params: any;
    this.isLoading = true;
    // const filterParam = this.filterForm.value;
    if (isExport) {
      params = {
        page: 0,
        size: 2000,
        fullName: this.filterForm.value.fullName,
        email: this.filterForm.value.email,
      };
    } else {
      const size = event?.rows ?? 10;
      const page = event?.first ? Math.floor(event.first / size) : '0';

      params = {
        page: page,
        size: size,
        fullName: this.filterForm.value.fullName,
        email: this.filterForm.value.email,
        mobileNumber: this.filterForm.value.mobileNumber,
      };
    }

    // Remove empty filters
    Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key];
      }
    });

    this.userService.getAllUsers(params).subscribe({
      next: (page: Page<AppUser>) => {
        this.users = page.content || [];
        this.totalRecords = page.totalElements;
        this.isLoading = false;
        if (callback) {
          callback(page.content);
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load users.',
        });
        console.error(err);
        this.isLoading = false;
      },
    });
  }

  getActiveCount(): number {
    return this.users.filter((appUser) => appUser.status === 'active').length;
  }

  getPendingCount(): number {
    return this.users.filter((appUser) => appUser.status === 'pending').length;
  }

  createUser(): void {
    this.router.navigate(
      ['.', { outlets: { canvasOutlet: ['users', 'create'] } }],
      { relativeTo: this.route.parent?.parent }
    );
  }

  editUser(id: number): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['users', 'edit', id] } },
    ]);
  }

  viewProfile(id: number): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['users', 'profile', id] } },
    ]);
  }

  exportExcel(): void {
    this.loadUsers(undefined, true, (users: AppUser[]) => {
      const dataToExport = users.map((user) => ({
        FullName: user.fullName,
        Email: user.email,
        'Mobile Number': user.mobileNumber,
        Roles: user.roles?.map((role) => role.roleName).join(', ') || '',
        // 'Brands': user.roles?.map(role => role.roleName).join(', ') || '',
      }));

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'users');
      XLSX.writeFile(wb, 'Users_Export.xlsx');
    });
  }
}
